import React from "react";

/**
 * Error boundary that shows a small non-blocking toast instead of a full red screen.
 * After ~8s it auto-reloads the current route (fixes most transient chunk / hydration errors).
 * User can click "Retry" to reset in-place, "Reload now" to reload immediately, or dismiss to try again.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, dismissed: false, countdown: 5 };
    this._timer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, dismissed: false, countdown: 5 };
  }

  componentDidCatch(error, info) {
    console.warn("Boundary caught:", error?.message, info?.componentStack);
    this._startCountdown();
  }

  componentWillUnmount() {
    if (this._timer) clearInterval(this._timer);
  }

  _startCountdown = () => {
    if (this._timer) clearInterval(this._timer);
    this._timer = setInterval(() => {
      this.setState((s) => {
        if (s.dismissed || !s.hasError) { clearInterval(this._timer); return null; }
        if (s.countdown <= 1) { clearInterval(this._timer); window.location.reload(); return { countdown: 0 }; }
        return { countdown: s.countdown - 1 };
      });
    }, 1000);
  };

  reset = () => {
    if (this._timer) clearInterval(this._timer);
    this.setState({ hasError: false, error: null, dismissed: false, countdown: 5 });
  };

  dismiss = () => {
    if (this._timer) clearInterval(this._timer);
    this.setState({ dismissed: true });
  };

  render() {
    const { hasError, error, dismissed, countdown } = this.state;
    return (
      <>
        {this.props.children}
        {hasError && !dismissed && (
          <div
            className="fixed bottom-6 right-6 z-[9999] max-w-sm bg-card border border-destructive/40 shadow-2xl rounded-md p-4 animate-in fade-in slide-in-from-bottom-4"
            role="alert"
            data-testid="error-boundary"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-destructive" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="overline text-destructive text-[10px] mb-1">A small hiccup</div>
                <div className="text-sm text-foreground leading-tight mb-1">
                  Something interrupted this page.
                </div>
                <div className="text-xs text-muted-foreground mb-3">
                  Auto-refreshing in <span className="font-mono text-foreground">{countdown}s</span>…
                  {error?.message ? (
                    <span className="block mt-1 opacity-60 truncate">({String(error.message).slice(0, 80)})</span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={this.reset}
                    className="text-xs px-3 py-1.5 rounded-full border border-primary/30 hover:border-primary hover:bg-primary/5"
                    data-testid="error-boundary-retry"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="error-boundary-reload"
                  >
                    Reload now
                  </button>
                  <button
                    onClick={this.dismiss}
                    className="text-xs px-2 py-1.5 text-muted-foreground hover:text-foreground"
                    data-testid="error-boundary-dismiss"
                    aria-label="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}
